import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useState } from 'react'
import { Offcanvas, Tab, Tabs, Form } from 'react-bootstrap'
import { toast } from 'sonner';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import Select from 'react-select';
import PropertyTypes from '../types/PropertyTypes';
import { privateApi } from '../../../api/api';
import { CreatePropertySchema, CreatePropertyType } from '../schema/CreatePropertySchema';
import { useCreateProperty } from '../hooks/useCreateProperty';
import CurrencyInputRef from '../../../shared/components/form/CurrencyInputRef';
import { useAuth } from '../../../auth/context/AuthProvider';
import useIndexRegions from '../../../services/region/hooks/useIndexRegions';
import { useRoles } from '../../roles/hooks/useRoles';
import { useCreateUserV4 } from '../../user-management-v4/hooks/useCreateUserV4';
import { useAttachPropertyV4 } from '../../user-management-v4/hooks/useAttachPropertyV4';
import { CreateUserFormData, CreateUserV4Schema } from '../../user-management-v4/schemas/CreateUserV4Schema';
import useSyncUserRoles from '../../../services/user-v4/hooks/useSyncUserRoles';

type OptionType = {
    value: string;
    label: string;
};

const MinStayTypeOption: OptionType[] = [
    { value: 'both', label: "Both" },
    { value: 'arrival', label: "Minimum Stay" },
    { value: 'through', label: "Maximum Stay" },
];

interface CreatePropertyDialogProps {
    show: boolean;
    onClose: () => void;
}

const CreatePropertyDialog: React.FC<CreatePropertyDialogProps> = ({ show, onClose }) => {
    const { user } = useAuth();
    const { data: regionsData } = useIndexRegions({ all: true });
    const [stateId, setStateId] = useState<number | null>(null);
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

    const {
        register,
        handleSubmit,
        formState: { errors },
        control,
        reset,
        watch,
        setError,
        setValue,
        clearErrors,
        unregister
    } = useForm<CreatePropertyType>({
        resolver: zodResolver(CreatePropertySchema),
        defaultValues: {
            enable_cvv: 0,
            state_length: 500,
            min_stay_type: 'both',
            mobile_number_country_code: '+62',
            primary_phone_country_code: '+62',
            region_id: null,
            preferred_currency: 'IDR',
        },
    });

    const queryClient = useQueryClient();

    // Opsi "create user for this property" — form user terpisah (bukan nested <form>),
    // divalidasi imperatif via userForm.trigger() saat submit utama diklik.
    const [createUserEnabled, setCreateUserEnabled] = useState(false);
    const [selectedRoles, setSelectedRoles] = useState<OptionType[]>([]);

    const userForm = useForm<CreateUserFormData>({
        resolver: zodResolver(CreateUserV4Schema),
        defaultValues: {
            name: '',
            email: '',
            phone: '',
            password: '',
            company_id: '',
        },
    });

    useEffect(() => {
        if (user?.company?.id) {
            userForm.reset({ company_id: user.company.id });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const { data: rolesData, isLoading: isRolesLoading, isError: isRolesError } = useRoles({
        name: 'roles',
        page: 1,
        limit: 100,
        search: '',
    });
    const roleOptions: OptionType[] = rolesData?.data
        ? rolesData.data.map((role: any) => ({ value: role.name, label: role.name }))
        : [];

    const { mutate: createUser } = useCreateUserV4();
    const { mutateAsync: attachPropertyAsync } = useAttachPropertyV4();
    const { mutateAsync: syncRolesAsync } = useSyncUserRoles();

    const handleClose = () => {
        reset();
        onClose();
        setSelectedCategory(null);
        userForm.reset();
        setCreateUserEnabled(false);
        setSelectedRoles([]);
    };

    const { mutate, isPending } = useCreateProperty();

    // Setelah property berhasil dibuat, buat user (kalau diaktifkan), lalu attach ke
    // property + sync role secara paralel — kegagalan salah satu langkah TIDAK membatalkan
    // property/user yang sudah terlanjur dibuat (tidak ada rollback, sama seperti pola
    // "assign role/property saat create user" sebelumnya).
    // userPayload/roleNames diterima sebagai PARAMETER (bukan baca live userForm.getValues()/
    // selectedRoles) karena handleClose() me-reset kedua form SEBELUM fungsi ini jalan —
    // baca live di sini akan selalu dapat nilai kosong hasil reset.
    const createUserForProperty = useCallback(async (
        propertyId: string,
        userPayload: CreateUserFormData,
        roleNames: string[]
    ) => {
        const userToastId = 'create-property-user';
        toast.loading('Creating user...', { id: userToastId });

        createUser(userPayload, {
            onSuccess: async (userResponse) => {
                const newUserId = userResponse?.data?.id;
                if (!newUserId) return;

                const tasks: { step: 'property' | 'role'; promise: Promise<unknown> }[] = [
                    { step: 'property', promise: attachPropertyAsync({ userId: newUserId, propertyId }) },
                ];
                if (roleNames.length > 0) {
                    tasks.push({ step: 'role', promise: syncRolesAsync({ user_id: newUserId, roles: roleNames }) });
                }

                const results = await Promise.allSettled(tasks.map(t => t.promise));
                const failedSteps = tasks.filter((_, idx) => results[idx].status === 'rejected').map(t => t.step);

                if (failedSteps.length === 0) {
                    toast.success('User created and linked to this property.', { id: userToastId });
                } else {
                    const label = failedSteps.join(' & ');
                    toast.error(`Property & user were created, but failed to assign ${label}. You can assign it manually from the user list.`, { id: userToastId });
                }
            },
            onError: (error) => {
                const errorMessage = (error as any)?.response?.data?.message || 'Failed to create user.';
                toast.error(`Property was created, but ${errorMessage} You can create the user manually from User Management.`, { id: userToastId });
            }
        });
    }, [createUser, attachPropertyAsync, syncRolesAsync]);

    const onSubmit = useCallback(async (values: CreatePropertyType) => {
        if (createUserEnabled) {
            const isUserFormValid = await userForm.trigger();
            if (!isUserFormValid) return;
        }

        // Capture nilai user form & role SEBELUM handleClose() (yang me-reset keduanya)
        // dipanggil di onSuccess di bawah — lihat catatan di createUserForProperty.
        const userPayload: Partial<CreateUserFormData> = { ...userForm.getValues() };
        if (!user?.company) {
            delete userPayload.company_id;
        }
        const roleNames = selectedRoles.map(r => r.value);

        toast.loading('Creating property...', { id: 'create-property' });
        mutate(values, {
            onSuccess: async (response) => {
                handleClose();

                await queryClient.refetchQueries({
                    predicate: (query) => query.queryKey.some((key) => ['properties'].includes(key as string))
                });

                toast.success('Property created successfully', { id: 'create-property' });

                const newPropertyId = (response as any)?.data?.data?.id;
                if (createUserEnabled && newPropertyId) {
                    createUserForProperty(newPropertyId, userPayload as CreateUserFormData, roleNames);
                }
            },
            onError: (mutationError) => {
                if ((mutationError as any)?.status === 422) {
                    const validationErrors = (mutationError as any)?.response?.data?.errors;
                    if (validationErrors) {
                        for (const [field, messages] of Object.entries(validationErrors)) {
                            setError(field as keyof CreatePropertyType, {
                                type: "manual",
                                message: (messages as string[])[0],
                            });
                        }
                        toast.error('Failed to create property', { id: 'create-property' });
                    }
                } else {
                    toast.error(mutationError?.message || 'Failed to create property', { id: 'create-property' });
                }
            }
        });
    }, [mutate, createUserEnabled, userForm, user, selectedRoles, createUserForProperty]);

    const cutOffTime = watch("cut_off_time");
    const [showMaxDaysAdvance, setShowMaxDaysAdvance] = useState(false);

    useEffect(() => {
        if (showMaxDaysAdvance) {
            // Set default value 365 saat enabled
            setValue("max_day_advance", 365);
            clearErrors("max_day_advance");
        } else {
            // Hapus field max_day_advance dari form saat disable
            unregister("max_day_advance");
        }
    }, [showMaxDaysAdvance, setValue, unregister, clearErrors]);

    return (
        <Offcanvas show={show} onHide={handleClose} placement='end' className="offcanvas-responsive">
            <Offcanvas.Header closeButton className='border-bottom-0'>
                <Offcanvas.Title>Create property</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body className='p-0'>
                <form
                    className="d-flex flex-column"
                    style={{ height: "100%" }}
                    onSubmit={handleSubmit(onSubmit)}
                >
                    <div className="action-sheet-content flex-grow-1">
                        <Tabs
                            defaultActiveKey="general"
                            variant='underline'
                            className="mb-3 text-primary px-4 border-bottom"
                        >
                            <Tab eventKey="general" title="General" className='px-4'>
                                {user?.company && (
                                    <input type="hidden" {...register("company_id", { value: user.company.id })} />
                                )}
                                <div className="row mb-3 align-items-center">
                                    <label
                                        htmlFor="name"
                                        className="col-sm-4 col-form-label d-flex justify-content-md-end"
                                    >
                                        Name:
                                    </label>
                                    <div className="col-sm-8">
                                        <input
                                            type="text"
                                            className={clsx(
                                                "form-control app-form-control rounded-1",
                                                errors.name && "is-invalid"
                                            )}
                                            id="name"
                                            placeholder="Name"
                                            {...register("name")}
                                        />
                                        {errors.name && (
                                            <div className="invalid-feedback">{errors.name.message}</div>
                                        )}
                                    </div>
                                </div>

                                <PropertyTypesSelectComponent control={control} errors={errors} onSelectedBillingType={setSelectedCategory} />

                                {selectedCategory && (
                                    <BillingTypeSelectComponent
                                        selectedCategory={selectedCategory}
                                    />
                                )}

                                <PreferredCurrencySelectComponent control={control} errors={errors} />

                                <TimezoneSelectComponent control={control} errors={errors} />

                                <div className="row mb-3 align-items-center">
                                    <label
                                        className="col-sm-4 col-form-label d-flex justify-content-md-end"
                                    >
                                        Check-in Time:
                                    </label>
                                    <div className="col-sm-8 d-flex align-items-center gap-2">
                                        <div>
                                            <input
                                                type="time"
                                                className={clsx(
                                                    "form-control app-form-control rounded-1",
                                                    errors.checkin_time_start && "is-invalid"
                                                )}
                                                {...register("checkin_time_start")}
                                            />
                                            {errors.checkin_time_start && (
                                                <div className="invalid-feedback d-block">{errors.checkin_time_start.message}</div>
                                            )}
                                        </div>
                                        <span>-</span>
                                        <div>
                                            <input
                                                type="time"
                                                className={clsx(
                                                    "form-control app-form-control rounded-1",
                                                    errors.checkin_time_end && "is-invalid"
                                                )}
                                                {...register("checkin_time_end")}
                                            />
                                            {errors.checkin_time_end && (
                                                <div className="invalid-feedback d-block">{errors.checkin_time_end.message}</div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="row mb-3 align-items-center">
                                    <label
                                        className="col-sm-4 col-form-label d-flex justify-content-md-end"
                                    >
                                        Check-out Time:
                                    </label>
                                    <div className="col-sm-8 d-flex align-items-center gap-2">
                                        <div>
                                            <input
                                                type="time"
                                                className={clsx(
                                                    "form-control app-form-control rounded-1",
                                                    errors.checkout_time_start && "is-invalid"
                                                )}
                                                {...register("checkout_time_start")}
                                            />
                                            {errors.checkout_time_start && (
                                                <div className="invalid-feedback d-block">{errors.checkout_time_start.message}</div>
                                            )}
                                        </div>
                                        <span>-</span>
                                        <div>
                                            <input
                                                type="time"
                                                className={clsx(
                                                    "form-control app-form-control rounded-1",
                                                    errors.checkout_time_end && "is-invalid"
                                                )}
                                                {...register("checkout_time_end")}
                                            />
                                            {errors.checkout_time_end && (
                                                <div className="invalid-feedback d-block">{errors.checkout_time_end.message}</div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="row mb-3 align-items-center">
                                    <label
                                        htmlFor="primary_email"
                                        className="col-sm-4 col-form-label d-flex justify-content-md-end"
                                    >
                                        Primary Email:
                                    </label>
                                    <div className="col-sm-8">
                                        <input
                                            type="email"
                                            className={clsx(
                                                "form-control app-form-control rounded-1",
                                                errors.primary_email && "is-invalid"
                                            )}
                                            id="primary_email"
                                            placeholder="Primary Email"
                                            {...register("primary_email")}
                                        />
                                        {errors.primary_email && (
                                            <div className="invalid-feedback">{errors.primary_email.message}</div>
                                        )}
                                    </div>
                                </div>

                                <CCEmailsComponent control={control} errors={errors} />

                                {/* accounting_email */}
                                <div className="row mb-3 align-items-center">
                                    <label
                                        htmlFor="accounting_email"
                                        className="col-sm-4 col-form-label d-flex justify-content-md-end"
                                    >
                                        Accounting Email:
                                    </label>
                                    <div className="col-sm-8">
                                        <input
                                            type="email"
                                            className={clsx(
                                                "form-control app-form-control rounded-1",
                                                errors.accounting_email && "is-invalid"
                                            )}
                                            id="accounting_email"
                                            placeholder="Accounting Email"
                                            {...register("accounting_email")}
                                        />
                                        {errors.accounting_email && (
                                            <div className="invalid-feedback">{errors.accounting_email.message}</div>
                                        )}
                                    </div>
                                </div>

                                <div className="row mb-3 align-items-center">
                                    <label
                                        htmlFor="email_autofill"
                                        className="col-sm-4 col-form-label d-flex justify-content-md-end"
                                    >
                                        Email Autofill:
                                    </label>
                                    <div className="col-sm-8">
                                        <input
                                            type="email"
                                            className={clsx(
                                                "form-control app-form-control rounded-1",
                                                errors.email_autofill && "is-invalid"
                                            )}
                                            id="email_autofill"
                                            placeholder="Email Autofill"
                                            {...register("email_autofill")}
                                        />
                                        {errors.email_autofill && (
                                            <div className="invalid-feedback">{errors.email_autofill.message}</div>
                                        )}
                                    </div>
                                </div>

                                <MobileNumberSelectComponent control={control} errors={errors} register={register} />

                                <PrimaryPhoneSelectComponent control={control} errors={errors} register={register} />

                                <div className="row mb-3 align-items-center">
                                    <label
                                        htmlFor="website"
                                        className="col-sm-4 col-form-label d-flex justify-content-md-end"
                                    >
                                        Website:
                                    </label>
                                    <div className="col-sm-8">
                                        <input
                                            type="text"
                                            className={clsx(
                                                "form-control app-form-control rounded-1",
                                                errors.website && "is-invalid"
                                            )}
                                            id="website"
                                            placeholder="Website"
                                            {...register("website")}
                                        />
                                        {errors.website && (
                                            <div className="invalid-feedback">{errors.website.message}</div>
                                        )}
                                    </div>
                                </div>

                                <h3 className="mt-6 border-bottom fw-normal">Location Settings</h3>

                                <CountrySelectComponent control={control} errors={errors} />

                                <RegionSelectComponent control={control} errors={errors} regionsData={regionsData} />

                                {watch("country") === "ID" && (
                                    <>
                                        <StateSelectComponent control={control} errors={errors} setState={setStateId} />
                                        {watch("state") && (
                                            <CitySelectComponent control={control} errors={errors} state={stateId} />
                                        )}
                                    </>
                                )}

                                <div className="row mb-3 align-items-center">
                                    <label
                                        htmlFor="street_address"
                                        className="col-sm-4 col-form-label d-flex justify-content-md-end"
                                    >
                                        Street Address:
                                    </label>
                                    <div className="col-sm-8">
                                        <input
                                            type="text"
                                            className={clsx(
                                                "form-control app-form-control rounded-1",
                                                errors.street_address && "is-invalid"
                                            )}
                                            id="street_address"
                                            placeholder="Street Address"
                                            {...register("street_address")}
                                        />
                                        {errors.street_address && (
                                            <div className="invalid-feedback">{errors.street_address.message}</div>
                                        )}
                                    </div>
                                </div>

                                <div className="row mb-3 align-items-center">
                                    <label
                                        htmlFor="postal_code"
                                        className="col-sm-4 col-form-label d-flex justify-content-md-end"
                                    >
                                        Postal Code:
                                    </label>
                                    <div className="col-sm-8">
                                        <input
                                            type="text"
                                            className={clsx(
                                                "form-control app-form-control rounded-1",
                                                errors.postal_code && "is-invalid"
                                            )}
                                            id="postal_code"
                                            placeholder="Postal Code"
                                            {...register("postal_code")}
                                        />
                                        {errors.postal_code && (
                                            <div className="invalid-feedback">{errors.postal_code.message}</div>
                                        )}
                                    </div>
                                </div>

                                <div className="row mb-3 align-items-center">
                                    <label
                                        htmlFor="enable_cvv"
                                        className="col-sm-4 col-form-label d-flex justify-content-md-end"
                                    >
                                        Enable CVV:
                                    </label>
                                    <div className="col-sm-8">
                                        <Controller
                                            name="enable_cvv"
                                            control={control}
                                            render={({ field }) => (
                                                <input
                                                    type="checkbox"
                                                    className={clsx(
                                                        "form-check-input app-form-control rounded-1",
                                                        errors.enable_cvv && "is-invalid"
                                                    )}
                                                    id="enable_cvv"
                                                    {...field}
                                                    value={field.value ?? 0}
                                                    onChange={(e) => field.onChange(e.target.checked ? 1 : 0)}
                                                />
                                            )}
                                        />
                                        {errors.enable_cvv && (
                                            <div className="invalid-feedback">{errors.enable_cvv.message}</div>
                                        )}
                                    </div>
                                </div>

                                <div className="row mb-3 align-items-center">
                                    <label htmlFor="logo" className="col-sm-4 col-form-label d-flex justify-content-md-end">
                                        Upload Logo:
                                    </label>
                                    <div className="col-sm-8">
                                        <Controller
                                            name="logo"
                                            control={control}
                                            render={({ field: { onChange, value } }) => {
                                                const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                                                    const file = e.target.files?.[0];
                                                    if (file) {
                                                        onChange(file);
                                                    }
                                                };

                                                return (
                                                    <>
                                                        <input
                                                            type="file"
                                                            className={clsx(
                                                                "form-control",
                                                                errors.logo && "is-invalid"
                                                            )}
                                                            id="logo"
                                                            accept="image/*"
                                                            onChange={handleFileChange}
                                                        />
                                                        {errors.logo && (
                                                            <div className="invalid-feedback">{String(errors.logo.message)}</div>
                                                        )}
                                                        {value && (
                                                            <div className="mt-2">
                                                                <img
                                                                    src={URL.createObjectURL(value)}
                                                                    alt="Logo Preview"
                                                                    className="img-thumbnail"
                                                                    style={{ maxWidth: "150px", maxHeight: "150px" }}
                                                                />
                                                            </div>
                                                        )}
                                                    </>
                                                );
                                            }}
                                        />
                                    </div>
                                </div>

                                <h3 className="mt-6 border-bottom fw-normal">User Access</h3>
                                <div className="row mb-3 align-items-center">
                                    <div className="col-sm-4 col-form-label d-flex justify-content-md-end" />
                                    <div className="col-sm-8">
                                        <Form.Check
                                            type="switch"
                                            id="create-user-switch"
                                            label="Create user for this property"
                                            checked={createUserEnabled}
                                            onChange={(e) => setCreateUserEnabled(e.target.checked)}
                                        />
                                    </div>
                                </div>

                                {createUserEnabled && (
                                    <>
                                        <div className="row mb-3 align-items-center">
                                            <label htmlFor="user-name" className="col-sm-4 col-form-label d-flex justify-content-md-end">
                                                Full Name:
                                            </label>
                                            <div className="col-sm-8">
                                                <input
                                                    id="user-name"
                                                    type="text"
                                                    className={clsx("form-control app-form-control rounded-1", userForm.formState.errors.name && "is-invalid")}
                                                    placeholder="e.g., John Doe"
                                                    {...userForm.register('name')}
                                                />
                                                {userForm.formState.errors.name && (
                                                    <div className="invalid-feedback">{userForm.formState.errors.name.message}</div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="row mb-3 align-items-center">
                                            <label htmlFor="user-email" className="col-sm-4 col-form-label d-flex justify-content-md-end">
                                                Email Address:
                                            </label>
                                            <div className="col-sm-8">
                                                <input
                                                    id="user-email"
                                                    type="email"
                                                    className={clsx("form-control app-form-control rounded-1", userForm.formState.errors.email && "is-invalid")}
                                                    placeholder="e.g., john.doe@example.com"
                                                    {...userForm.register('email')}
                                                />
                                                {userForm.formState.errors.email && (
                                                    <div className="invalid-feedback">{userForm.formState.errors.email.message}</div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="row mb-3 align-items-center">
                                            <label htmlFor="user-phone" className="col-sm-4 col-form-label d-flex justify-content-md-end">
                                                Phone Number:
                                            </label>
                                            <div className="col-sm-8">
                                                <input
                                                    id="user-phone"
                                                    type="text"
                                                    className={clsx("form-control app-form-control rounded-1", userForm.formState.errors.phone && "is-invalid")}
                                                    placeholder="e.g., 081234567890"
                                                    {...userForm.register('phone')}
                                                />
                                                {userForm.formState.errors.phone && (
                                                    <div className="invalid-feedback">{userForm.formState.errors.phone.message}</div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="row mb-3 align-items-center">
                                            <label htmlFor="user-password" className="col-sm-4 col-form-label d-flex justify-content-md-end">
                                                Password:
                                            </label>
                                            <div className="col-sm-8">
                                                <input
                                                    id="user-password"
                                                    type="password"
                                                    className={clsx("form-control app-form-control rounded-1", userForm.formState.errors.password && "is-invalid")}
                                                    placeholder="Minimum 8 characters"
                                                    {...userForm.register('password')}
                                                />
                                                {userForm.formState.errors.password && (
                                                    <div className="invalid-feedback">{userForm.formState.errors.password.message}</div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="row mb-3 align-items-center">
                                            <label className="col-sm-4 col-form-label d-flex justify-content-md-end">
                                                Assign Role(s):
                                            </label>
                                            <div className="col-sm-8">
                                                {isRolesError ? (
                                                    <div className="text-danger small">Failed to load roles. You can assign a role later from the user list.</div>
                                                ) : (
                                                    <Select
                                                        isMulti
                                                        classNamePrefix="react-select"
                                                        options={roleOptions}
                                                        value={selectedRoles}
                                                        onChange={(opts) => setSelectedRoles(opts as OptionType[])}
                                                        placeholder="Select role(s) to assign..."
                                                        isLoading={isRolesLoading}
                                                        isDisabled={isRolesLoading}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </Tab>
                            <Tab eventKey="settings" title="Settings" className='px-4' disabled>
                                <h3 className="border-bottom fw-normal">Price Setting</h3>
                                <div className="row mb-3 align-items-center">
                                    <label
                                        htmlFor="name"
                                        className="col-sm-4 col-form-label d-flex justify-content-md-end"
                                    >
                                        Min Price:
                                    </label>
                                    <div className="col-sm-8">
                                        <Controller
                                            name="min_price"
                                            control={control}
                                            render={({ field, fieldState }) => (
                                                <CurrencyInputRef
                                                    name={field.name}
                                                    value={field.value ?? 0}
                                                    onChange={(_e, rawValue) => {
                                                        field.onChange(Number(rawValue));
                                                    }}
                                                    error={fieldState.error?.message}
                                                    ref={field.ref} // ini akan bekerja karena kita sudah pakai forwardRef
                                                />
                                            )}
                                        />
                                    </div>
                                </div>
                                <div className="row mb-3 align-items-center">
                                    <label
                                        htmlFor="name"
                                        className="col-sm-4 col-form-label d-flex justify-content-md-end"
                                    >
                                        Max Price:
                                    </label>
                                    <div className="col-sm-8">
                                        <Controller
                                            name="max_price"
                                            control={control}
                                            render={({ field, fieldState }) => (
                                                <CurrencyInputRef
                                                    name={field.name}
                                                    value={field.value ?? 0}
                                                    onChange={(_e, rawValue) => {
                                                        field.onChange(Number(rawValue));
                                                    }}
                                                    error={fieldState.error?.message}
                                                    ref={field.ref} // ini akan bekerja karena kita sudah pakai forwardRef
                                                />
                                            )}
                                        />
                                    </div>
                                </div>
                                <div className="row mb-3 align-items-center">
                                    <div className="col-sm-4 col-form-label d-flex justify-content-md-end" />
                                    <div className="col-sm-8">
                                        <span className="text-muted">
                                            Use this section to configure the lowest and highest acceptable price for the property.
                                        </span>
                                    </div>
                                </div>
                                <h3 className='border-bottom fw-normal'>Inventory Days</h3>
                                <div className="row mb-3 align-items-center">
                                    <label
                                        htmlFor="name"
                                        className="col-sm-4 col-form-label d-flex justify-content-md-end"
                                    >
                                        Inventory Days:
                                    </label>
                                    <div className="col-sm-8">
                                        <input
                                            type="number"
                                            className={clsx(
                                                "form-control app-form-control rounded-1",
                                                errors.state_length && "is-invalid"
                                            )}
                                            id="state_length"
                                            placeholder="Inventory Days"
                                            {...register("state_length")}
                                        />
                                        {errors.state_length && (
                                            <div className="invalid-feedback">{errors.state_length.message}</div>
                                        )}
                                    </div>
                                </div>
                                <div className="row mb-3 align-items-center">
                                    <div className="col-sm-4 col-form-label d-flex justify-content-md-end" />
                                    <div className="col-sm-8">
                                        <span className="text-muted">
                                            Define the inventory table’s duration in days. Allowed range: 100–730 days.
                                        </span>
                                    </div>
                                </div>
                                <h3 className='border-bottom fw-normal mb-2'>Minimum Stay Settings</h3>
                                <div className="row mb-3 align-items-center">
                                    <p className="text-muted mb-2">
                                        Optimize the user experience by standardizing to a single minimum stay type. <br />
                                    </p>
                                    <p className="text-muted">
                                        Note: Changing this configuration will remove all existing data associated with the unused minimum stay type. To ensure consistency, please perform a full synchronization from your PMS after making any changes and verify the results accordingly.
                                    </p>
                                    <label
                                        htmlFor="name"
                                        className="col-sm-4 col-form-label d-flex justify-content-md-end"
                                    >
                                        Minimum Stay Setting:
                                    </label>
                                    <div className="col-sm-8">
                                        <Controller
                                            name="min_stay_type"
                                            control={control}
                                            render={({ field }) => (
                                                <Select<OptionType>
                                                    {...field}
                                                    options={MinStayTypeOption}
                                                    placeholder="Select minimum stay type"
                                                    isClearable
                                                    className={clsx(
                                                        "app-form-control rounded-1",
                                                        errors.min_stay_type && "is-invalid"
                                                    )}
                                                    onChange={(selectedOption) => {
                                                        field.onChange(selectedOption?.value);
                                                    }}
                                                    value={MinStayTypeOption.find(
                                                        (option) => option.value === field.value
                                                    )}
                                                />
                                            )}
                                        />
                                        {errors.min_stay_type && (
                                            <div className="invalid-feedback">{errors.min_stay_type.message}</div>
                                        )}
                                    </div>
                                </div>
                                <h3 className='border-bottom fw-normal mb-2'>Cut Off Time</h3>
                                <div className="row mb-3 align-items-center">
                                    <p className='text-muted mb-2'>
                                        Control the cutoff time for accepting same-day reservations.
                                    </p>
                                    <p className='text-muted'>
                                        The property’s local timezone will be used. Once the specified time is reached, availability for the current day will automatically be set to zero.
                                    </p>
                                    <label
                                        htmlFor="name"
                                        className="col-sm-4 col-form-label d-flex justify-content-md-end"
                                    >
                                        Cut Off Time:
                                    </label>
                                    <div className="col-sm-8">
                                        <select
                                            className={clsx(
                                                "form-control app-form-control rounded-1",
                                                errors.cut_off_time && "is-invalid"
                                            )}
                                            id="cut_off_time"
                                            {...register("cut_off_time")}
                                        >
                                            <option value="">None</option>
                                            {Array.from({ length: 48 }).map((_, index) => {
                                                const hour = Math.floor(index / 2)
                                                    .toString()
                                                    .padStart(2, "0");
                                                const minute = index % 2 === 0 ? "00" : "30";
                                                const label = `${hour}:${minute}`;
                                                const value = `${hour}:${minute}:00`;
                                                return (
                                                    <option key={value} value={value}>
                                                        {label}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                        {errors.cut_off_time && (
                                            <div className="invalid-feedback">{errors.cut_off_time.message}</div>
                                        )}
                                    </div>
                                </div>
                                {cutOffTime && cutOffTime !== "" && (
                                    <div className="row mb-3 align-items-center">
                                        <label
                                            htmlFor="cut_off_days"
                                            className="col-sm-4 col-form-label d-flex justify-content-md-end"
                                        >
                                            Cut off days:
                                        </label>
                                        <div className="col-sm-8">
                                            <select
                                                className={clsx(
                                                    "form-control app-form-control rounded-1",
                                                    errors.cut_off_days && "is-invalid"
                                                )}
                                                id="cut_off_days"
                                                {...register("cut_off_days")}
                                            >
                                                {Array.from({ length: 31 }).map((_, index) => {
                                                    const label = index === 0 ? "Same day" : `${index} days`;
                                                    return (
                                                        <option key={index} value={index.toString()}>
                                                            {label}
                                                        </option>
                                                    );
                                                })}
                                            </select>
                                            {errors.cut_off_days && (
                                                <div className="invalid-feedback">{errors.cut_off_days.message}</div>
                                            )}
                                        </div>
                                    </div>
                                )}
                                <h3 className='border-bottom fw-normal mb-2'>Max Days Advance</h3>
                                <div className="row mb-3 align-items-center">
                                    <p className="text-muted mb-2">
                                        Manually close availability for specific future dates.
                                    </p>
                                    <p className="text-muted">
                                        Please note that availability will not reopen automatically and must be managed manually.
                                    </p>

                                    <label
                                        htmlFor="toggle_max_days_advance"
                                        className="col-sm-4 col-form-label d-flex justify-content-md-end align-items-center"
                                    >
                                        <input
                                            type="checkbox"
                                            id="toggle_max_days_advance"
                                            className="form-check-input me-2"
                                            checked={showMaxDaysAdvance}
                                            onChange={(e) => setShowMaxDaysAdvance(e.target.checked)}
                                        />
                                        Enable Max Days Advance
                                    </label>

                                    {showMaxDaysAdvance && (
                                        <div className="col-sm-8">
                                            <input
                                                type="number"
                                                className={clsx(
                                                    "form-control app-form-control rounded-1",
                                                    errors.max_day_advance && "is-invalid"
                                                )}
                                                id="max_day_advance"
                                                placeholder="Max Days Advance"
                                                {...register("max_day_advance")}
                                            />
                                            {errors.max_day_advance && (
                                                <div className="invalid-feedback">{errors.max_day_advance.message}</div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </Tab>
                        </Tabs>
                        <div className="form-group mt-auto d-grid px-4">
                            <button
                                type="submit"
                                className="btn btn-primary btn-block"
                                disabled={isPending}
                            >
                                {isPending ? "Submitting..." : "Create Property"}
                            </button>
                        </div>
                    </div>
                </form>
            </Offcanvas.Body>
        </Offcanvas>
    )
}

export default CreatePropertyDialog

interface PropertyTypesSelectComponentProps {
    control: any;
    errors: any;
    onSelectedBillingType?: (value: string | null) => void;
}

const PropertyTypesSelectComponent: React.FC<PropertyTypesSelectComponentProps> = ({ control, errors, onSelectedBillingType }) => {
    const PropertyTypesOptions = PropertyTypes.map((propertyType) => ({
        value: propertyType.value,
        label: propertyType.label,
        category: propertyType.category,
    }));

    return (
        <div className='row mb-3 align-items-center'>
            <label
                htmlFor="property_type"
                className="col-sm-4 col-form-label d-flex justify-content-md-end"
            >
                Property Type:
            </label>
            <div className="col-sm-8">
                <Controller
                    name="property_type"
                    control={control}
                    render={({ field }) => (
                        <Select
                            {...field}
                            options={PropertyTypesOptions}
                            placeholder="Select property type"
                            isClearable
                            className={clsx(
                                "app-form-control rounded-1",
                                errors.property_type && "is-invalid"
                            )}
                            onChange={(selectedOption) => {
                                field.onChange(selectedOption?.value);
                                if (onSelectedBillingType) {
                                    onSelectedBillingType(selectedOption?.category ?? null);
                                }
                            }}
                            value={PropertyTypesOptions.find(
                                (option) => option.value === field.value
                            )}
                        />
                    )}
                />
                {errors.property_type && (
                    <div className="invalid-feedback">{errors.property_type.message}</div>
                )}
            </div>
        </div>
    );
};

interface BillingTypeSelectComponentProps {
    selectedCategory: string;
}

const BillingTypeSelectComponent: React.FC<BillingTypeSelectComponentProps> = ({ selectedCategory }) => {
    return (
        <div className='row mb-3 align-items-center'>
            <label
                htmlFor="property_type"
                className="col-sm-4 col-form-label d-flex justify-content-md-end"
            >
                Billing Type:
            </label>
            <div className="col-sm-8">
                <span className='form-control'>{selectedCategory}</span>
            </div>
        </div>
    );
}

interface PreferredCurrencySelectComponentProps {
    control: any;
    errors: any;
}

const PreferredCurrencySelectComponent: React.FC<PreferredCurrencySelectComponentProps> = ({ control, errors }) => {
    // Sistem saat ini HANYA support IDR (lihat IndexAi/property-management.md) --
    // dropdown ini SENGAJA dikunci ke IDR (isDisabled + options difilter cuma
    // 1 item) sampai ada currency lain yang benar-benar didukung end-to-end
    // (payment, invoice, dsb), bukan cuma tersedia di daftar /v3/currency-list.
    const CurrencyOptions = useQuery({
        queryKey: ["currencies"],
        queryFn: () => privateApi.get('/v3/currency-list').then((res) => res.data.data
            .map((currency: { value: string; text: string }) => ({
                value: currency.value,
                label: currency.text,
            }))
            .filter((currency: { value: string }) => currency.value === 'IDR')),
    })

    return (
        <div className='row mb-3 align-items-center'>
            <label
                htmlFor="preferred_currency"
                className="col-sm-4 col-form-label d-flex justify-content-md-end"
            >
                Preferred Currency:
            </label>
            <div className="col-sm-8">
                <Controller
                    name="preferred_currency"
                    control={control}
                    render={({ field }) => (
                        <Select
                            {...field}
                            options={CurrencyOptions.data}
                            isLoading={CurrencyOptions.isFetching}
                            placeholder="Select preferred currency"
                            isDisabled
                            className={clsx(
                                "app-form-control rounded-1",
                                errors.preferred_currency && "is-invalid"
                            )}
                            value={CurrencyOptions.data?.find(
                                (option: {
                                    value: string;
                                    text: string;
                                }) => option.value === field.value
                            ) ?? { value: 'IDR', label: 'IDR' }}
                            onChange={(selectedOption) => {
                                field.onChange(selectedOption?.value);
                            }}
                        />
                    )}
                />
                <small className="text-muted d-block mt-1">Currently only IDR is supported.</small>
                {errors.preferred_currency && (
                    <div className="invalid-feedback">{errors.preferred_currency.message}</div>
                )}
            </div>
        </div>
    );
};

interface TimezoneSelectComponentProps {
    control: any;
    errors: any;
}

const TimezoneSelectComponent: React.FC<TimezoneSelectComponentProps> = ({ control, errors }) => {
    const TimezoneOptions = useQuery({
        queryKey: ["timezones"],
        queryFn: () => privateApi.get('/v3/timezone-list').then((res) => res.data.data.map((timezone: { value: string; text: string }) => ({
            value: timezone.value,
            label: timezone.text,
        }))),
    })

    return (
        <div className='row mb-3 align-items-center'>
            <label
                htmlFor="time_zone"
                className="col-sm-4 col-form-label d-flex justify-content-md-end"
            >
                Timezone:
            </label>
            <div className="col-sm-8">
                <Controller
                    name="time_zone"
                    control={control}
                    render={({ field }) => (
                        <Select
                            {...field}
                            options={TimezoneOptions.data}
                            isLoading={TimezoneOptions.isFetching}
                            placeholder="Select timezone"
                            isClearable
                            className={clsx(
                                "app-form-control rounded-1",
                                errors.time_zone && "is-invalid"
                            )}
                            value={TimezoneOptions.data?.find(
                                (option: {
                                    value: string;
                                    text: string;
                                }) => option.value === field.value
                            )}
                            onChange={(selectedOption) => {
                                field.onChange(selectedOption?.value);
                            }}
                        />
                    )}
                />
                {errors.time_zone && (
                    <div className="invalid-feedback">{errors.time_zone.message}</div>
                )}
            </div>
        </div>
    );
};

interface PrimaryPhoneSelectComponentProps {
    control: any;
    errors: any;
    register: any;
}

const PrimaryPhoneSelectComponent: React.FC<PrimaryPhoneSelectComponentProps> = ({ control, errors, register }) => {
    const PhoneOptions = useQuery({
        queryKey: ["dialcode_list"],
        queryFn: () => privateApi.get('/v3/dialcode-list').then((res) => res.data.data.map((country: { value: string; text: string }) => ({
            value: country.value,
            label: country.text,
        }))),
    });

    return (
        <div className="row mb-3 align-items-center">
            <label
                htmlFor='primary_phone'
                className="col-sm-4 col-form-label d-flex justify-content-md-end"
            >
                Primary Phone
            </label>
            <div className="col-sm-4">
                <Controller
                    name="primary_phone_country_code"
                    control={control}
                    render={({ field }) => (
                        <Select
                            {...field}
                            options={PhoneOptions.data}
                            isLoading={PhoneOptions.isFetching}
                            placeholder="+62"
                            isClearable
                            className={clsx(
                                "app-form-control rounded-1",
                                errors.primary_phone_country_code && "is-invalid"
                            )}
                            value={PhoneOptions.data?.find(
                                (option: {
                                    value: string;
                                    text: string;
                                }) => option.value === field.value
                            )}
                            onChange={(selectedOption) => {
                                field.onChange(selectedOption?.value);
                            }}
                        />
                    )}
                />
            </div>
            <div className="col-sm-4">
                <input
                    type='text'
                    className={clsx("form-control app-form-control rounded-1", errors.primary_phone && "is-invalid")}
                    id='primary_phone'
                    placeholder='81234567890'
                    name='primary_phone'
                    {...register('primary_phone')}
                />
                {errors.primary_phone && (
                    <div className="invalid-feedback">{errors.primary_phone.message}</div>
                )}

                {errors.primary_phone_country_code && (
                    <div className="invalid-feedback">{errors.primary_phone_country_code.message}</div>
                )}
            </div>
        </div>
    )
};

interface MobileNumberSelectComponentProps {
    control: any;
    errors: any;
    register: any;
}

const MobileNumberSelectComponent: React.FC<MobileNumberSelectComponentProps> = ({ control, errors, register }) => {
    const PhoneOptions = useQuery({
        queryKey: ["dialcode_list"],
        queryFn: () => privateApi.get('/v3/dialcode-list').then((res) => res.data.data.map((country: { value: string; text: string }) => ({
            value: country.value,
            label: country.text,
        }))),
    });

    return (
        <div className="row mb-3 align-items-center">
            <label
                htmlFor='mobile_number'
                className="col-sm-4 col-form-label d-flex justify-content-md-end"
            >
                Mobile Number
            </label>
            <div className="col-sm-4">
                <Controller
                    name="mobile_number_country_code"
                    control={control}
                    render={({ field }) => (
                        <Select
                            {...field}
                            options={PhoneOptions.data}
                            isLoading={PhoneOptions.isFetching}
                            placeholder="+62"
                            isClearable
                            className={clsx(
                                "app-form-control rounded-1",
                                errors.mobile_number_country_code && "is-invalid"
                            )}
                            value={PhoneOptions.data?.find(
                                (option: {
                                    value: string;
                                    text: string;
                                }) => option.value === field.value
                            )}
                            onChange={(selectedOption) => {
                                field.onChange(selectedOption?.value);
                            }}
                        />
                    )}
                />
            </div>
            <div className="col-sm-4">
                <input
                    type='text'
                    className={clsx("form-control app-form-control rounded-1", errors.mobile_number && "is-invalid")}
                    id='mobile_number'
                    placeholder='81234567890'
                    name='mobile_number'
                    {...register('mobile_number')}
                />
                {errors.mobile_number && (
                    <div className="invalid-feedback">{errors.mobile_number.message}</div>
                )}

                {errors.mobile_number_country_code && (
                    <div className="invalid-feedback">{errors.mobile_number_country_code.message}</div>
                )}
            </div>
        </div>
    )
};

interface CCEmailsComponentProps {
    control: any;
    errors: any;
}

const CCEmailsComponent: React.FC<CCEmailsComponentProps> = ({ control, errors }) => {
    const [rawCcEmails, setRawCcEmails] = useState("");

    const parseCcEmails = (rawEmails: string) => {
        // Pisahkan berdasarkan spasi, koma, atau newline
        return rawEmails
            .split(/[\s,]+/) // Memisahkan berdasarkan spasi, koma, atau newline
            .map((email) => email.trim()) // Hilangkan spasi ekstra di sekitar email
            .filter((email) => email); // Hapus elemen kosong
    };

    return (
        <div className="row mb-3 align-items-center">
            <label
                htmlFor="cc_emails"
                className="col-sm-4 col-form-label d-flex justify-content-md-end"
            >
                CC Emails:
            </label>
            <div className="col-sm-8">
                <Controller
                    name="cc_emails"
                    control={control}
                    render={({ field }) => (
                        <>
                            <textarea
                                {...field}
                                className={clsx(
                                    "form-control app-form-control rounded-1",
                                    errors.cc_emails && "is-invalid"
                                )}
                                id="cc_emails"
                                placeholder="Enter multiple emails, separated by commas or new lines"
                                value={rawCcEmails}
                                onChange={(e) => {
                                    const rawValue = e.target.value;
                                    setRawCcEmails(rawValue);
                                    field.onChange(parseCcEmails(rawValue)); // Menghubungkan dengan React Hook Form
                                }}
                                rows={3}
                            />
                            {/* {errors.cc_emails && (
                                <div className="invalid-feedback">{errors.cc_emails.message}</div>
                            )} */}
                            {errors.cc_emails && (
                                console.log(errors.cc_emails),
                                <div className="invalid-feedback">
                                    {Array.isArray(errors.cc_emails)
                                        ? errors.cc_emails.map((error: any, index: number) => (
                                            <div key={index}>{error.message}</div>
                                        ))
                                        : errors.cc_emails.message}
                                </div>
                            )}
                        </>
                    )}
                />
            </div>
        </div>
    );
};

interface CountrySelectComponentProps {
    control: any;
    errors: any;
}

const CountrySelectComponent: React.FC<CountrySelectComponentProps> = ({ control, errors }) => {
    const CountryOptions = useQuery({
        queryKey: ["countries"],
        queryFn: () => privateApi.get('/v3/country-list').then((res) => res.data.data.map((country: { value: string; text: string }) => ({
            value: country.value,
            label: country.text,
        }))),
    });

    return (
        <div className='row mb-3 align-items-center'>
            <label
                htmlFor="country"
                className="col-sm-4 col-form-label d-flex justify-content-md-end"
            >
                Country:
            </label>
            <div className="col-sm-8">
                <Controller
                    name="country"
                    control={control}
                    defaultValue={null}
                    render={({ field }) => (
                        <Select
                            {...field}
                            options={CountryOptions.data}
                            isLoading={CountryOptions.isFetching}
                            placeholder="Select country"
                            isClearable
                            className={clsx(
                                "app-form-control rounded-1",
                                errors.country && "is-invalid"
                            )}
                            value={CountryOptions.data?.find(
                                (option: {
                                    value: string;
                                    text: string;
                                }) => option.value === field.value
                            )}
                            onChange={(selectedOption) => {
                                field.onChange(selectedOption?.value);
                            }}
                        />
                    )}
                />
                {errors.country && (
                    <div className="invalid-feedback">{errors.country.message}</div>
                )}
            </div>
        </div>
    );
};

interface StateSelectComponentProps {
    control: any;
    errors: any;
    setState: (stateId: number | null) => void;
}

const StateSelectComponent: React.FC<StateSelectComponentProps> = ({ control, errors, setState }) => {
    const StateOptions = useQuery({
        queryKey: ["states"],
        queryFn: () => privateApi.get('/v3/provinces').then((res) => res.data.data.map((state: { id: string; name: string }) => ({
            value: state.id,
            label: state.name,
        }))),
    });

    return (
        <div className='row mb-3 align-items-center'>
            <label
                htmlFor="state"
                className="col-sm-4 col-form-label d-flex justify-content-md-end"
            >
                State:
            </label>
            <div className="col-sm-8">
                <Controller
                    name="state"
                    control={control}
                    render={({ field }) => (
                        <Select
                            {...field}
                            options={StateOptions.data}
                            isLoading={StateOptions.isFetching}
                            placeholder="Select state"
                            isClearable
                            className={clsx(
                                "app-form-control rounded-1",
                                errors.state && "is-invalid"
                            )}
                            value={StateOptions.data?.find(
                                (option: {
                                    value: string;
                                    text: string;
                                }) => option.value === field.value
                            )}
                            onChange={(selectedOption) => {
                                field.onChange(selectedOption?.label);
                                setState(selectedOption ? Number(selectedOption.value) : null);
                            }}
                        />
                    )}
                />
                {errors.state && (
                    <div className="invalid-feedback">{errors.state.message}</div>
                )}
            </div>
        </div>
    );
};

interface CitySelectComponentProps {
    control: any;
    errors: any;
    state: number | null;
}

const CitySelectComponent: React.FC<CitySelectComponentProps> = ({ control, errors, state }) => {

    const CityOptions = useQuery({
        queryKey: ["cities", state],
        queryFn: () => privateApi.get(`/v3/regencies/${state}`).then((res) => res.data.data.map((city: { id: string; name: string }) => ({
            value: city.id,
            label: city.name,
        }))),
        enabled: !!state,
    });

    return (
        <div className='row mb-3 align-items-center'>
            <label
                htmlFor="city"
                className="col-sm-4 col-form-label d-flex justify-content-md-end"
            >
                City:
            </label>
            <div className="col-sm-8">
                <Controller
                    name="city"
                    control={control}
                    defaultValue={null}
                    render={({ field }) => (
                        <Select
                            {...field}
                            options={CityOptions.data}
                            isLoading={CityOptions.isFetching}
                            placeholder="Select city"
                            isClearable
                            className={clsx(
                                "app-form-control rounded-1",
                                errors.city && "is-invalid"
                            )}
                            value={CityOptions.data?.find(
                                (option: {
                                    value: string;
                                    text: string;
                                }) => option.value === field.value
                            )}
                            onChange={(selectedOption) => {
                                field.onChange(selectedOption?.label);
                            }}
                        />
                    )}
                />
                {errors.city && (
                    <div className="invalid-feedback">{errors.city.message}</div>
                )}
            </div>
        </div>
    );
};

interface RegionSelectComponentProps {
    control: any;
    errors: any;
    regionsData: any;
}

const RegionSelectComponent: React.FC<RegionSelectComponentProps> = ({ control, errors, regionsData }) => {
    const regions = regionsData?.data ?? [];
    return (
        <div className="row mb-3 align-items-center">
            <label htmlFor="region_id" className="col-sm-4 col-form-label d-flex justify-content-md-end">Region:</label>
            <div className="col-sm-8">
                <Controller
                    name="region_id"
                    control={control}
                    render={({ field }) => (
                        <select
                            {...field}
                            id="region_id"
                            className={clsx("form-select app-form-control rounded-1", errors.region_id && "is-invalid")}
                            value={field.value ?? ""}
                            onChange={(e) => field.onChange(e.target.value === "" ? null : e.target.value)}
                        >
                            <option value="">Select Region</option>
                            {regions.map((region: any) => (
                                <option key={region.id} value={region.id}>{region.name}</option>
                            ))}
                        </select>
                    )}
                />
                {errors.region_id && <div className="invalid-feedback">{typeof errors.region_id?.message === 'string' ? errors.region_id.message : ''}</div>}
            </div>
        </div>
    );
};